import type { LayoutServerLoad, LayoutServerLoadEvent } from '../../routes/(app)/[language=language]/$types';
import type { ApiSchemas } from '$lib/api/shopwareApi.js';
import { shopwareApiClient } from '$lib/api/shopwareApi.js';
import type { PropertiesContext, ShopContext } from '$lib/context.js';
import type { SupportedLocale } from '$lib/i18n/initI18n.js';
import { AGE_CHECK_COOKIE_NAME } from '$lib/cookies.js';
import { isbot } from 'isbot';

export const undefinedShopContext = { storeContext: {}, shopwareLanguage: {} } as ShopContext;

async function findMatchingShopwareLanguage(languageCode: string): Promise<ApiSchemas['Language'] | undefined> {
    const shopwareLanguages = await shopwareApiClient.invoke(
        'readLanguages post /language',
        {},
        { fetchImplementation: fetch, shopContext: undefinedShopContext }
    );

    const possibleLanguages =
        shopwareLanguages.elements?.filter((lang) => lang.translationCode?.code.startsWith(languageCode.slice(2))) ??
        [];

    const exactMatch = possibleLanguages.find(
        (lang) => lang.translationCode?.code === languageCode + '-' + languageCode.toUpperCase()
    );

    return exactMatch ?? possibleLanguages[0];
}

export async function loadShopContext(
    { fetch, cookies, params }: Pick<LayoutServerLoadEvent, 'fetch' | 'cookies' | 'params'>,
    overrideLanguage?: SupportedLocale
): Promise<ShopContext> {
    const [shopwareLanguage, storeContext] = await Promise.all([
        findMatchingShopwareLanguage(overrideLanguage ?? params.language),
        shopwareApiClient.invoke(
            'readContext get /context',
            {},
            {
                fetchImplementation: fetch,
                headers: cookies.get('sw-context-token')
                    ? { 'sw-context-token': cookies.get('sw-context-token')! }
                    : {},
                shopContext: undefinedShopContext
            }
        )
    ]);

    if (!shopwareLanguage) {
        throw new Error('No matching shopware language found for language ' + (overrideLanguage ?? params.language));
    }

    return {
        storeContext,
        shopwareLanguage: {
            languageId: shopwareLanguage.id!,
            shopwareLanguage: shopwareLanguage
        }
    } satisfies ShopContext;
}

export const loadShopLayoutServer = (language?: SupportedLocale) =>
    (async ({ fetch, cookies, params, request }) => {
        const userAgent = request.headers.get('user-agent');
        const isBot = userAgent ? isbot(userAgent) : false;
        // Skip age check for bots such as Googlebot
        const ageCheckCookie = isBot ? 'true' : cookies.get(AGE_CHECK_COOKIE_NAME);

        const [shopwareLanguage, storeContext, shopProperties] = await Promise.all([
            findMatchingShopwareLanguage(language ?? params.language),
            shopwareApiClient.invoke(
                'readContext get /context',
                {},
                {
                    fetchImplementation: fetch,
                    headers: cookies.get('sw-context-token')
                        ? { 'sw-context-token': cookies.get('sw-context-token')! }
                        : {},
                    shopContext: undefinedShopContext
                }
            ),
            shopwareApiClient.invoke('readProperties get /properties', {}, { shopContext: undefinedShopContext })
        ]);

        if (!shopwareLanguage) {
            throw new Error('No matching shopware language found for language ' + (language ?? params.language));
        }

        return {
            shopProperties,
            shopContext: {
                storeContext,
                shopwareLanguage: {
                    languageId: shopwareLanguage.id!,
                    shopwareLanguage: shopwareLanguage
                }
            } satisfies ShopContext,
            ageCheckCookie
        };
    }) satisfies LayoutServerLoad<{ shopProperties: PropertiesContext; shopContext: ShopContext }>;
